import {
  hero,
  heroCss,
  joinLabels,
  kitPartCss,
  pageLabel,
  panel,
  panelCss,
  positive,
  readingTime,
} from '../armKit';
import { deepWorkFacts } from '../pageData';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';

type Spotlight = {
  workId: number;
  title: string;
  url: string;
  downloadUrl: string;
  summary: string;
  pageCount: number | null;
};

const FEATURE_CLASS = 'profile-liautotest-landing__feature';
const TITLE_SOURCE = 'profile-liautotest-landing-title';
const READ_SOURCE = 'profile-liautotest-landing-read';
const DOWNLOAD_SOURCE = 'profile-liautotest-landing-download';
const READ_LABEL = 'Read';
const DOWNLOAD_LABEL = 'Download';

// The accent blue is the arm's own — the palette holds none.
function featureCss(scope: string): string {
  return `
${scope} .${FEATURE_CLASS} {
  border-left: 3px solid #0645b1;
  padding: 20px 24px;
}
`;
}

// A page count is meta-line garnish, never a gate: a paper of unknown length
// still carries the hero, its figures line just stays out.
function candidateOf(workId: number, facts: WorkFacts): Spotlight | null {
  const { downloadUrl, summary, title, url } = facts;

  if (downloadUrl == null) return null;
  if (summary == null || summary.trim() === '') return null;
  if (title == null || title === '' || url == null || url === '') return null;

  return {
    workId,
    title,
    url,
    downloadUrl,
    summary,
    pageCount: positive(facts.pageCount),
  };
}

// The newest wins: the highest work id is the most recently added, and a file
// plus an abstract stay the gate. Views are deliberately never consulted —
// recency and a downloadable file are the predictors this arm banks on, and
// popularity measured null.
function spotlightOf(facts: Map<number, WorkFacts>): Spotlight | null {
  let best: Spotlight | null = null;

  for (const [workId, entry] of facts) {
    const candidate = candidateOf(workId, entry);
    if (candidate != null && (best == null || workId > best.workId)) {
      best = candidate;
    }
  }

  return best;
}

function featurePanel(spotlight: Spotlight, ctx: ArmContext): HTMLElement {
  const { root, body } = panel();
  root.classList.add(FEATURE_CLASS);

  const track = (source: string) => (): void => {
    ctx.track(source, { entityId: spotlight.workId });
  };

  hero(body, {
    title: spotlight.title,
    href: spotlight.url,
    onTitleClick: track(TITLE_SOURCE),
    meta: joinLabels([
      pageLabel(spotlight.pageCount),
      readingTime(spotlight.pageCount),
    ]),
    // The COMPLETE summary, unclamped: the full abstract IS the design.
    abstract: spotlight.summary,
    actions: [
      { label: READ_LABEL, href: spotlight.url, onClick: track(READ_SOURCE) },
      {
        label: DOWNLOAD_LABEL,
        href: spotlight.downloadUrl,
        onClick: track(DOWNLOAD_SOURCE),
      },
    ],
  });

  return root;
}

export const onePaperLanding: ArmSpec = {
  // stamp stays unset: the gate is the deepWorkFacts fetch, which no
  // synchronous prediction can see, so render_state rides markRendered alone.
  onReady: async (ctx) => {
    const spotlight = spotlightOf(await deepWorkFacts(ctx.viewedUserId));
    if (spotlight == null) return;

    const slot = ctx.mountSlot('listHero');
    if (slot == null) return;

    ctx.injectStyles(
      panelCss(ctx.scope) +
        heroCss(ctx.scope) +
        // Georgia at normal weight is the page's own work-card title face; the
        // spotlight keeps only the size to itself.
        kitPartCss(
          'heroTitle',
          ctx.scope,
          'font-size: 24px; line-height: 1.25; ' +
            'font-family: Georgia, serif; font-weight: 400;',
        ) +
        featureCss(ctx.scope),
    );

    // The list below stays complete: the spotlight paper keeps its card, so
    // it appearing twice on the page is by design, never a bug to dedupe.
    slot.appendChild(featurePanel(spotlight, ctx));
    ctx.markRendered();
  },
};
